import { useState } from 'react';
import { LangCode, getTranslation } from '../../../components/feature/LanguageSelector';

interface Property {
  id: number;
  title: string;
  price: string;
}

interface PropertyContactProps {
  property: Property;
  lang: LangCode;
}

export default function PropertyContact({ property, lang }: PropertyContactProps) {
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', message: '' });
  const [status, setStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');

  const t = (key: string) => getTranslation(lang, key);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.phone) {
      setStatus('error');
      return;
    }
    setStatus('sending');
    try {
      const body = new URLSearchParams({
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        message: formData.message || `${t('prop_contact_interested')} ${property.title}`,
      }).toString();
      const res = await fetch('https://readdy.ai/api/form/d6ng78aqoe30lj0v8npg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body,
      });
      if (res.ok) {
        setStatus('success');
        setFormData({ name: '', email: '', phone: '', message: '' });
        setTimeout(() => setStatus('idle'), 4000);
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };

  return (
    <div className="sticky top-24">
      {/* Price card */}
      <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-6 mb-4">
        <p className="text-2xl font-bold text-gray-900 mb-1">{property.price}</p>
        <p className="text-gray-500 text-sm mb-5">{t('prop_contact_price_note')}</p>

        <a
          href="https://wa.me/34744780446"
          target="_blank"
          rel="noopener noreferrer nofollow"
          className="flex items-center justify-center gap-2 w-full py-3.5 rounded-xl font-semibold text-white transition-colors cursor-pointer whitespace-nowrap mb-3"
          style={{ backgroundColor: '#25D366' }}
        >
          <i className="ri-whatsapp-line text-lg"></i>
          {t('prop_contact_whatsapp')}
        </a>

        <a
          href="tel:+34744780446"
          className="flex items-center justify-center gap-2 w-full py-3.5 rounded-xl font-semibold text-gray-900 border-2 border-gray-200 hover:border-gray-400 transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className="ri-phone-line text-lg"></i>
          +34 744 780 446
        </a>
      </div>

      {/* Contact form */}
      <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-6">
        <h3 className="text-lg font-bold text-gray-900 mb-5">{t('prop_contact_form_title')}</h3>

        <form data-readdy-form onSubmit={handleSubmit} className="space-y-4">
          <input
            type="text"
            name="name"
            placeholder={t('prop_contact_name')}
            value={formData.name}
            onChange={e => setFormData({ ...formData, name: e.target.value })}
            className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-red-400 focus:ring-2 focus:ring-red-100 transition-all"
            required
          />
          <input
            type="email"
            name="email"
            placeholder={t('prop_contact_email')}
            value={formData.email}
            onChange={e => setFormData({ ...formData, email: e.target.value })}
            className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-red-400 focus:ring-2 focus:ring-red-100 transition-all"
            required
          />
          <input
            type="tel"
            name="phone"
            placeholder={t('prop_contact_phone')}
            value={formData.phone}
            onChange={e => setFormData({ ...formData, phone: e.target.value })}
            className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-red-400 focus:ring-2 focus:ring-red-100 transition-all"
            required
          />
          <textarea
            name="message"
            placeholder={`${t('prop_contact_interested')} ${property.title}`}
            value={formData.message}
            onChange={e => setFormData({ ...formData, message: e.target.value })}
            maxLength={500}
            rows={3}
            className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-red-400 focus:ring-2 focus:ring-red-100 transition-all resize-none"
          />

          {status === 'success' && (
            <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-xl text-sm text-center">
              {t('prop_contact_success')}
            </div>
          )}
          {status === 'error' && (
            <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-xl text-sm text-center">
              {t('prop_contact_error')}
            </div>
          )}

          <button
            type="submit"
            disabled={status === 'sending'}
            className="w-full bg-red-600 hover:bg-red-700 text-white py-3.5 rounded-xl font-semibold transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
          >
            {status === 'sending' ? t('prop_contact_sending') : t('prop_contact_send')}
          </button>
        </form>
      </div>
    </div>
  );
}
